import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-apollo';

import { AlbumFolderForHierarchyGrades } from '../../../graphql/queries/album-folder';
import { AlbumForHierarchyGrades } from '../../../graphql/queries/albums';
import { albumFolderUpdate as albumFolderUpdateMutation } from '../../../graphql/mutations/album-folder';
import { albumUpdate as albumUpdateMutation } from '../../../graphql/mutations/albums';

const useHierarchyGrades = ({ albumFolderId, albumId } = {}) => {
  const { data: { albumFolder, me: meForAlbumFolderAlbums } = {}, loading } =
    useQuery(AlbumFolderForHierarchyGrades, {
      variables: {
        id: albumFolderId,
      },
      skip: !albumFolderId,
      fetchPolicy: 'cache-and-network',
    });
  const { data: { album } = {} } = useQuery(AlbumForHierarchyGrades, {
    variables: {
      id: albumId,
    },
    skip: !albumId,
    fetchPolicy: 'cache-and-network',
  });

  const [albumFolderUpdate] = useMutation(albumFolderUpdateMutation);
  const [albumUpdate] = useMutation(albumUpdateMutation);

  const onHierarchyGradeCreate = async ({
    albumFolderId: albumFolderIdToUse = albumFolderId,
    albumId: albumIdToUse = albumId,
    hierarchyGrade,
  }) => {
    let result;
    if (albumFolderIdToUse) {
      result = await albumFolderUpdate({
        variables: {
          input: {
            album_folder_id: albumFolderIdToUse,
            grades: {
              create_hierarchy_grades: [hierarchyGrade],
            },
          },
        },
      });
    } else if (albumIdToUse) {
      result = await albumUpdate({
        variables: {
          input: {
            id: albumIdToUse,
            grades: {
              create_hierarchy_grades: [hierarchyGrade],
            },
          },
        },
      });
    }

    return result;
  };

  const onHierarchyGradeClassCreate = async ({
    albumFolderId: albumFolderIdToUse = albumFolderId,
    albumId: albumIdToUse = albumId,
    hierarchyGradeId,
    hierarchyGradeClass,
  }) => {
    let result;

    if (albumFolderIdToUse) {
      result = await albumFolderUpdate({
        variables: {
          input: {
            album_folder_id: albumFolderIdToUse,
            grades: {
              update_hierarchy_grades: [
                {
                  hierarchy_grade_id: hierarchyGradeId,
                  create_hierarchy_grade_classes: [hierarchyGradeClass],
                },
              ],
            },
          },
        },
      });
    } else if (albumIdToUse) {
      result = await albumUpdate({
        variables: {
          input: {
            id: albumIdToUse,
            grades: {
              update_hierarchy_grades: [
                {
                  hierarchy_grade_id: hierarchyGradeId,
                  create_hierarchy_grade_classes: [hierarchyGradeClass],
                },
              ],
            },
          },
        },
      });
    }

    return result;
  };

  const onHierarchyGradeDelete = async ({
    albumFolderId: albumFolderIdToUse = albumFolderId,
    albumId: albumIdToUse = albumId,
    hierarchyGradeId,
  }) => {
    let result;

    if (albumFolderIdToUse) {
      result = await albumFolderUpdate({
        variables: {
          input: {
            album_folder_id: albumFolderIdToUse,
            grades: {
              delete_hierarchy_grade_ids: [hierarchyGradeId],
            },
          },
        },
      });
    } else if (albumIdToUse) {
      result = await albumUpdate({
        variables: {
          input: {
            id: albumIdToUse,
            grades: {
              delete_hierarchy_grade_ids: [hierarchyGradeId],
            },
          },
        },
      });
    }

    return result;
  };

  const grades = useMemo(
    () => albumFolder?.grades?.records ?? album?.grades?.records ?? [],
    [albumFolder, album],
  );

  const getLockedClassNames = grade => {
    if (!grade || !meForAlbumFolderAlbums?.albums?.records?.length) {
      return [];
    }

    const lockedClassNames = [];

    meForAlbumFolderAlbums.albums.records.forEach(albumWithGrades => {
      const albumGrade = albumWithGrades.grades.records.find(
        hierarchyGrade => hierarchyGrade.grade === grade,
      );

      (albumGrade?.classes?.records ?? [])?.forEach(hierarchyGradeClass =>
        lockedClassNames.push(hierarchyGradeClass.class_name),
      );
    });

    return lockedClassNames;
  };

  return {
    loading,
    grades,
    getLockedClassNames,
    allowsMultiSelect: true,
    onHierarchyGradeCreate,
    onHierarchyGradeClassCreate,
    onHierarchyGradeDelete,
  };
};

export default useHierarchyGrades;
